/**
 * Card component migrated from @wineryxplorer/design-system
 * Uses brand-tokens via Tailwind CSS
 */

import { clsx } from 'clsx';
import type { ReactNode } from 'react';

export interface CardProps {
  variant?: 'elevated' | 'outlined' | 'filled';
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

const cardVariants = {
  elevated: 'bg-gray-50 shadow-lg border border-gray-200',
  outlined: 'bg-gray-50 border-2 border-gray-200',
  filled: 'bg-gray-100 border border-gray-200',
};

const cardPadding = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
  xl: 'p-8',
};

export const Card = ({
  variant = 'elevated',
  padding = 'md',
  children,
  onClick,
  disabled = false,
  className,
  ...props
}: CardProps) => {
  const baseClasses = 'rounded-lg transition-all duration-200';
  const variantClasses = cardVariants[variant];
  const paddingClasses = cardPadding[padding];

  const interactiveClasses = onClick && !disabled
    ? 'cursor-pointer hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
    : '';

  const disabledClasses = disabled
    ? 'opacity-50 cursor-not-allowed'
    : '';

  const Component = onClick ? 'button' : 'div';

  return (
    <Component
      onClick={onClick && !disabled ? onClick : undefined}
      disabled={onClick ? disabled : undefined}
      className={clsx(
        baseClasses,
        variantClasses,
        paddingClasses,
        interactiveClasses,
        disabledClasses,
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};
